import { SubathonSoundId } from './types';

/**
 * Sonidos de cierre del extensible, sintetizados con Web Audio (no hay archivos).
 * Todos son suaves y cortos. Este archivo está espejado en el panel admin:
 * si tocás una receta acá, tocala también en
 * frontend/src/components/subathon/subathonSounds.ts.
 */

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return null;
    if (!ctx) ctx = new Ctx();
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

interface ToneOptions {
  freq: number;
  type?: OscillatorType;
  at?: number;
  duration?: number;
  gain?: number;
  attack?: number;
  filter?: number;
  detune?: number;
}

function tone(audio: AudioContext, master: GainNode, options: ToneOptions) {
  const {
    freq,
    type = 'sine',
    at = 0,
    duration = 0.6,
    gain = 0.2,
    attack = 0.01,
    filter,
    detune,
  } = options;

  const start = audio.currentTime + at;
  const osc = audio.createOscillator();
  const env = audio.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (detune) osc.detune.setValueAtTime(detune, start);

  env.gain.setValueAtTime(0.0001, start);
  env.gain.linearRampToValueAtTime(gain, start + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  let node: AudioNode = env;
  if (filter) {
    const lp = audio.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(filter, start);
    env.connect(lp);
    node = lp;
  }

  osc.connect(env);
  node.connect(master);
  osc.start(start);
  osc.stop(start + duration + 0.05);
}

function noiseSwell(
  audio: AudioContext,
  master: GainNode,
  at: number,
  duration: number,
  gain: number,
  cutoff: number,
) {
  const start = audio.currentTime + at;
  const frames = Math.floor(audio.sampleRate * duration);
  const buffer = audio.createBuffer(1, frames, audio.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) {
    data[i] = (Math.random() * 2 - 1) * 0.6;
  }

  const source = audio.createBufferSource();
  source.buffer = buffer;

  const lp = audio.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(cutoff, start);

  const env = audio.createGain();
  env.gain.setValueAtTime(0.0001, start);
  env.gain.linearRampToValueAtTime(gain, start + duration * 0.45);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  source.connect(lp);
  lp.connect(env);
  env.connect(master);
  source.start(start);
  source.stop(start + duration);
}

type Recipe = (audio: AudioContext, master: GainNode) => void;

const RECIPES: Record<Exclude<SubathonSoundId, 'none'>, Recipe> = {
  chime: (audio, master) => {
    tone(audio, master, { freq: 523.25, duration: 0.8, gain: 0.2 });
    tone(audio, master, { freq: 659.25, at: 0.16, duration: 0.9, gain: 0.16 });
    tone(audio, master, { freq: 783.99, at: 0.32, duration: 1.0, gain: 0.12 });
  },

  bell: (audio, master) => {
    tone(audio, master, { freq: 880, type: 'triangle', duration: 1.8, gain: 0.2, attack: 0.005 });
    tone(audio, master, { freq: 1318.5, type: 'triangle', duration: 1.2, gain: 0.07, attack: 0.005 });
    tone(audio, master, { freq: 2637, duration: 0.5, gain: 0.03, attack: 0.003 });
  },

  marimba: (audio, master) => {
    [587.33, 783.99, 1046.5].forEach((freq, index) => {
      tone(audio, master, {
        freq,
        at: index * 0.11,
        duration: 0.45,
        gain: 0.22,
        attack: 0.004,
        filter: 2600,
      });
    });
  },

  bowl: (audio, master) => {
    tone(audio, master, { freq: 196, duration: 3.2, gain: 0.18, attack: 0.08, filter: 1400 });
    tone(audio, master, { freq: 293.66, at: 0.05, duration: 2.8, gain: 0.09, attack: 0.1, filter: 1600 });
    tone(audio, master, { freq: 198.5, duration: 3.0, gain: 0.06, attack: 0.12 });
  },

  harp: (audio, master) => {
    [392, 493.88, 587.33, 783.99, 987.77].forEach((freq, index) => {
      tone(audio, master, {
        freq,
        type: 'triangle',
        at: index * 0.065,
        duration: 1.1,
        gain: 0.13,
        attack: 0.005,
        filter: 3400,
      });
    });
  },

  music_box: (audio, master) => {
    [1046.5, 1318.5, 1567.98].forEach((freq, index) => {
      tone(audio, master, {
        freq,
        at: index * 0.16,
        duration: 1.2,
        gain: 0.1,
        attack: 0.004,
        filter: 4200,
      });
    });
    tone(audio, master, { freq: 523.25, at: 0.02, duration: 1.4, gain: 0.05, attack: 0.01 });
  },

  kalimba: (audio, master) => {
    [659.25, 880, 1174.66].forEach((freq, index) => {
      tone(audio, master, {
        freq,
        type: 'triangle',
        at: index * 0.13,
        duration: 0.7,
        gain: 0.16,
        attack: 0.003,
        filter: 3000,
      });
    });
  },

  glass: (audio, master) => {
    tone(audio, master, { freq: 1567.98, duration: 0.9, gain: 0.1, attack: 0.003 });
    tone(audio, master, { freq: 2093, at: 0.05, duration: 0.7, gain: 0.06, attack: 0.003 });
    tone(audio, master, { freq: 3135.96, at: 0.1, duration: 0.5, gain: 0.03, attack: 0.002 });
  },

  warm_pad: (audio, master) => {
    tone(audio, master, { freq: 261.63, duration: 2.6, gain: 0.12, attack: 0.5, filter: 1200 });
    tone(audio, master, { freq: 329.63, duration: 2.6, gain: 0.1, attack: 0.6, filter: 1200, detune: 6 });
    tone(audio, master, { freq: 392, duration: 2.4, gain: 0.08, attack: 0.7, filter: 1400, detune: -6 });
  },

  soft_gong: (audio, master) => {
    tone(audio, master, { freq: 146.83, type: 'triangle', duration: 3.0, gain: 0.15, attack: 0.03, filter: 800 });
    tone(audio, master, { freq: 220, duration: 2.4, gain: 0.08, attack: 0.05, filter: 1000 });
    noiseSwell(audio, master, 0, 0.4, 0.04, 480);
  },

  wood: (audio, master) => {
    tone(audio, master, { freq: 320, type: 'triangle', duration: 0.18, gain: 0.2, attack: 0.002, filter: 1800 });
    tone(audio, master, { freq: 480, at: 0.14, duration: 0.2, gain: 0.14, attack: 0.002, filter: 2000 });
  },

  twinkle: (audio, master) => {
    [1318.5, 1760, 2093, 2637].forEach((freq, index) => {
      tone(audio, master, {
        freq,
        at: index * 0.07,
        duration: 0.5,
        gain: 0.08,
        attack: 0.003,
        filter: 5000,
      });
    });
  },
};

export function playSubathonSound(id: SubathonSoundId | undefined, volume: number) {
  if (!id || id === 'none') return;
  const recipe = RECIPES[id as Exclude<SubathonSoundId, 'none'>];
  if (!recipe) return;

  const audio = getContext();
  if (!audio) return;

  try {
    const master = audio.createGain();
    master.gain.value = Math.max(0, Math.min(1, volume / 100));
    master.connect(audio.destination);
    recipe(audio, master);
  } catch {
    /* el overlay nunca debe romperse por el audio */
  }
}
